import React, { useState, useEffect } from 'react'; 
import { useNavigate } from 'react-router-dom';
import api from './api';
import { 
  Button, Container, TextField, Typography, Box, 
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle 
} from '@mui/material';

function LoginPage() {
  // --- AUTOMATIC YEAR VARIABLE ---
  const currentYear = new Date().getFullYear();
  // -------------------------------

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // State for the Forgot Password Dialog
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  // --- SEO: Set Page Title ---
  useEffect(() => {
    document.title = "Login | Christmas Lights Map";
    
    // Cleanup using the dynamic year
    return () => {
      document.title = `Christmas Lights Map ${currentYear}`;
    };
  }, [currentYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await api.post('/login', {
        username: username,
        password: password
      });
      const token = response.data.token;
      localStorage.setItem('token', token);
      window.dispatchEvent(new Event('storageChanged')); 
      navigate('/');
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message);
      } else {
        setError('Login failed. Please try again.');
      }
    }
  };

  const handleForgotSubmit = async () => {
    try {
      const res = await api.post('/forgot-credentials', { email: forgotEmail });
      setForgotMessage(res.data.message);
    } catch (err) {
      setForgotMessage("Error sending email. Please try again.");
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          Login
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Login
          </Button>
          
          <Button 
            fullWidth 
            size="small" 
            onClick={() => {
                setForgotOpen(true);
                setForgotMessage('');
            }}
            sx={{ mt: 1 }}
          >
            Forgot Username or Password?
          </Button>

          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)}>
        <DialogTitle>Recover Credentials</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter your email address. We will send you your username and a link to reset your password.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
          />
          {forgotMessage && <Typography color="primary" sx={{ mt: 2 }}>{forgotMessage}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotOpen(false)}>Close</Button>
          <Button onClick={handleForgotSubmit}>Send Email</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}

export default LoginPage;